
/**
 * Generates an Excel SUM formula for a given field over a range of rows.
 *
 * The formula is constructed using the column letter (calculated from the field name)
 * and a vertical range of rows based on the provided start index and range length.
 *
 * @param headers - Array of header strings that map to Excel columns.
 * @param field - The name of the field whose column will be summed.
 * @param rowIndex - The starting row index (zero-based).
 * @param addLen - Number of rows to include in the SUM range.
 * @returns A string representing the Excel SUM formula (e.g., 'SUM(B5:B10)').
 *
 * @example
 * headers = ['Date', 'Investment', 'Fair value'];
 * field = 'Investment';
 * rowIndex = 4;
 * addLen = 6;
 * generateSumFormula(headers, field, rowIndex, addLen); // => 'SUM(B5:B10)'
 */
export const generateSumFormula = (headers: string[], field: string, rowIndex: number, addLen: number): string => {
  const index = headers.indexOf(field);
  const letter = columnIndexToExcelLetter(index);
  return `SUM(${letter}${rowIndex + 1}:${letter}${rowIndex + addLen})`;
};

export const generateSumFormulaVertical = (headers: string[], field: string, rowIndex: number, addLen: number): string => {
  const index = headers.indexOf(field);
  const letter = columnIndexToExcelLetter(index);
  return `SUM(${letter}${rowIndex - addLen}:${letter}${rowIndex - 1})`;
};

/**
 * Generates an Excel formula to calculate gain/loss as the difference between
 * fair market value (FMV) and cost for a specific row.
 *
 * The formula uses Excel column letters derived from the provided headers array.
 * Example output: 'D10-C10'
 *
 * @param headers - Array of header strings that map to Excel columns.
 * @param costName - The header name for the cost column.
 * @param fmvName - The header name for the fair market value column.
 * @param rowIndex - The row index (1-based Excel row number).
 * @returns A string representing the Excel formula for gain/loss.
 *
 * @example
 * headers = ['Investment', 'Cost', 'Fair value'];
 * costName = 'Cost';
 * fmvName = 'Fair value';
 * rowIndex = 10;
 * generateGainLossFormula(headers, costName, fmvName, rowIndex); // => 'C10-B10'
 */
export const generateGainLossFormula = (headers: string[], costName: string, fmvName: string, rowIndex: number): string => {
  const indexCost = headers.indexOf(costName);
  const letterCost = columnIndexToExcelLetter(indexCost);
  const indexFmv = headers.indexOf(fmvName);
  const letterFmv = columnIndexToExcelLetter(indexFmv);
  return `IFERROR(${letterFmv}${rowIndex}-${letterCost}${rowIndex},0)`;
};


export const generateMinusFormula = (headers: string[], first: string, second: string, rowIndex: number): string => {
  const indexCost = headers.indexOf(first);
  const letterCost = columnIndexToExcelLetter(indexCost);
  const indexFmv = headers.indexOf(second);
  const letterFmv = columnIndexToExcelLetter(indexFmv);
  return `IFERROR(${letterFmv}${rowIndex}-${letterCost}${rowIndex},0)`;
};
/**
 * Generates an Excel subtraction formula between two fields for a given row.
 *
 * @param headers - List of headers used to locate column indexes.
 * @param minuendField - The field (column name) to subtract from (i.e., the larger value — like FMV).
 * @param subtrahendField - The field (column name) to subtract (i.e., the smaller value — like Cost).
 * @param rowIndex - The Excel row index (1-based).
 * @returns A string representing the Excel subtraction formula (e.g., "C5-B5").
 *
 * @example
 * generateSubtractionFormula(headers, 'Fair value', 'Investment', 5);
 * // Returns: "C5-B5"
 */
export const generateSubtractionFormulaWithMinusBefore = (headers: string[], minuendField: string, subtrahendField: string, rowIndex: number
): string => {
  const minuendCol = columnIndexToExcelLetter(headers.indexOf(minuendField));
  const subtrahendCol = columnIndexToExcelLetter(headers.indexOf(subtrahendField));
  return `-${minuendCol}${rowIndex}-${subtrahendCol}${rowIndex}`;
};

/**
 * Generates an Excel formula to calculate MOIC (Multiple on Invested Capital)
 * as the ratio of fair market value (FMV) to cost for a specific row.
 *
 * The formula uses Excel column letters derived from the provided headers array
 * and wraps the result in an IFERROR to safely return 0 in case of division errors.
 * Example output: 'IFERROR(D10/C10,0)'
 *
 * @param headers - Array of header strings that map to Excel columns.
 * @param costName - The header name for the cost/investment column.
 * @param fmvName - The header name for the fair market value column.
 * @param rowIndex - The row index (1-based Excel row number).
 * @returns A string representing the Excel formula for MOIC.
 *
 * @example
 * headers = ['Investment', 'Cost', 'Fair value'];
 * costName = 'Cost';
 * fmvName = 'Fair value';
 * rowIndex = 10;
 * generateMoicFormula(headers, costName, fmvName, rowIndex); // => 'IFERROR(C10/B10,0)'
 */
export const generateMoicFormula = (headers: string[], costName: string, fmvName: string, rowIndex: number): string => {
  const indexCost = headers.indexOf(costName);
  const letterCost = columnIndexToExcelLetter(indexCost);
  const indexFmv = headers.indexOf(fmvName);
  const letterFmv = columnIndexToExcelLetter(indexFmv);
  return `IFERROR(${letterFmv}${rowIndex}/${letterCost}${rowIndex},0)`;
};

/**
 * Converts a zero-based column index to its corresponding Excel column letter.
 *
 * Excel columns are labeled alphabetically:
 * - 0 => 'A'
 * - 1 => 'B'
 * - ...
 * - 27 => 'AB'
 * - ...
 *
 * @param index - The zero-based column index (e.g., 0 for 'A', 1 for 'B', ..., 26 for 'AA').
 * @returns The Excel-style column letter(s).
 *
 * @example
 * exampleArray = ['Date', 'Investment', 'Fair value]
 * let index = exampleArray.indexOf('Investment') => 1
 * columnIndexToExcelLetter(index);   // "B"
 * let index = exampleArray.indexOf('Fair value') => 2
 * columnIndexToExcelLetter(index);  => "C"
 * columnIndexToExcelLetter(27);  => "AB"
 */
export const columnIndexToExcelLetter = (index: number): string => {
  let letter = '';
  let temp = index;

  while (temp >= 0) {
    letter = String.fromCharCode((temp % 26) + 65) + letter;
    temp = Math.floor(temp / 26) - 1;
  }

  return letter;
};
/** * Converts a header string to its corresponding Excel column letter based on the headers array.
 * * This function finds the index of the header in the headers array and then converts that index
 * to an Excel column letter using `columnIndexToExcelLetter`.
 * * @param header - The header string to convert (e.g., 'Investment').
 * @param headers - An array of header strings that map to Excel columns (e.g., ['Company', 'Investment', 'Fair value']).
 * * @returns The Excel column letter corresponding to the header (e.g., 'B' for 'Investment').
 * * @throws Error if the header is not found in the headers array.
 * @example
 * headers = ['Company', 'Investment', 'Fair value'];
 * headerToColumnLetter('Investment', headers); // => 'B'
 * headerToColumnLetter('Fair value', headers); // => 'C'
 * headerToColumnLetter('Company', headers); // => 'A'
 */
export const headerToColumnLetter = (header: string, headers: string[]): string => {
  const index = headers.indexOf(header);
  if (index === -1) {
    throw new Error(`Header "${header}" not found in headers array.`);
  }
  return columnIndexToExcelLetter(index);
};


/**
 * Generates an Excel SUM formula for a specific field across multiple rows (e.g., for subtotals or grand totals).
 *
 * The formula uses the field's column letter and row indexes provided in `totalIndexArray`.
 *
 * @param field - The name of the header whose column will be summed.
 * @param headers - Array of header strings that map to Excel columns.
 * @param totalIndexArray - An array of row indexes (1-based) to include in the SUM formula.
 * @returns A string representing the Excel SUM formula or '0' if the array is empty.
 *
 * @example
 * headers = ['Company', 'Investment', 'Fair value'];
 * field = 'Investment';
 * totalIndexArray = [5, 10, 15, 19];
 * generateExcelSumFormulaForGrandOrSubTotal(field, headers, totalIndexArray); // => 'SUM(B5,B10,B15,B19)'
 */
export const generateExcelSumFormulaForGrandOrSubTotal = (field: string, headers: string[], totalIndexArray: number[]): string => {
  const columnIndex = headers.indexOf(field);
  const letter = columnIndexToExcelLetter(columnIndex);
  if (!totalIndexArray || totalIndexArray.length === 0) {
    return '0';
  }
  const columnReference = letter;
  return `SUM(${totalIndexArray.map((index) => `${columnReference}${index}`).join(',')})`;
};

export const nullToUndefined = (value: number): number => (value ? value : undefined);



export const generateRegularSumFormulaForTotal = (headers: string[], field: string, startIndex: number, stopIndex: number): string => {
  const columnIndex = headers.indexOf(field);
  const letter = columnIndexToExcelLetter(columnIndex);
  return `SUM(${letter}${startIndex}:${letter}${stopIndex})`;
};
/**
 * Generates an Excel SUM formula for summing cells in a single row,
 * from startLetter to endLetter, for the given row index.
 *
 * Example: generateSumFormulaForRow('B', 'E', 5) => "SUM(B5:E5)"
 *
 * @param {string} startLetter - The starting column letter (e.g., 'B').
 * @param {string} endLetter - The ending column letter (e.g., 'E').
 * @param {number} currentRowIndex - The row index to use in the formula.
 * @returns {string} The Excel SUM formula as a string.
 */
export const generateSumFormulaForRow = (startLetter: string, endLetter: string, currentRowIndex: number): string => {
  return `SUM(${startLetter}${currentRowIndex}:${endLetter}${currentRowIndex})`;
};

/**
 * Generates an Excel formula to calculate the average for a year
 * by summing cells in a single row (from startLetter to endLetter)
 * and dividing the sum by 4 (assuming there are 4 quarters).
 *
 * Example: generateSumFormulaAverageForYear('B', 'E', 5) => "SUM(B5:E5)/4"
 *
 * @param {string} startLetter - The starting column letter (e.g., 'B').
 * @param {string} endLetter - The ending column letter (e.g., 'E').
 * @param {number} currentRowIndex - The row index to use in the formula.
 * @returns {string} The Excel formula to calculate the average over 4 quarters.
 */
export const generateSumFormulaAverageForYear = (startLetter: string, endLetter: string, currentRowIndex: number): string => {
  return `SUM(${startLetter}${currentRowIndex}:${endLetter}${currentRowIndex})/4`;
};

/**
 * Generates an Excel SUM formula for summing non-consecutive cells
 * in the same row based on an array of column indexes.
 *
 * Example: generateSumFormulaForRowForYears([1, 3, 5], 5) => "SUM(B5,D5,F5)"
 * @param {number[]} columnIndexes - Array of column indexes (0-based), which will be converted to letters.
 * @param {number} rowIndex - The row index to use in the formula.
 * @returns {string} The Excel SUM formula summing the specified cells.
 */
export const generateSumFormulaForRowForYears = (columnIndexes: number[], rowIndex: number): string => {
  const columnLetters = columnIndexes.map((index) => columnIndexToExcelLetter(index));
  const cells = columnLetters.map((col) => `${col}${rowIndex}`);
  return `SUM(${cells.join(',')})`;
};

/**
 * Generates an Excel division formula (e.g., B5 / A5) wrapped in IFERROR to handle divide-by-zero cases.
 *
 * It determines the column letters for the numerator and denominator headers, and constructs a formula
 * that divides one cell by another in the same row.
 *
 * @param headers - Array of header strings that map to Excel columns.
 * @param numeratorName - The name of the header to use as the numerator (e.g., 'Total commitment').
 * @param denominatorName - The name of the header to use as the denominator (e.g., 'Commitment').
 * @param rowIndex - The 1-based row number where the formula should be applied.
 * @returns A string representing the Excel division formula (wrapped in IFERROR).
 *
 * @example
 * headers = ['Commitment', 'Total commitment', 'MOIC'];
 * numeratorName = 'Commitment';
 * denominatorName = 'Total commitment';
 * rowIndex = 5;
 * generateDivisionFormula(headers, 'Commitment', 'Total commitment', 5); // => 'IFERROR(A5/B5,"")'
 */
export const generateDivisionFormula = (headers: string[], numeratorName: string, denominatorName: string, rowIndex: number): string => {
  const indexNumerator = headers.indexOf(numeratorName);
  const letterNumerator = columnIndexToExcelLetter(indexNumerator);
  const indexDenominator = headers.indexOf(denominatorName);
  const letterDenominator = columnIndexToExcelLetter(indexDenominator);
  return `IFERROR(${letterNumerator}${rowIndex}/${letterDenominator}${rowIndex},"")`;
};

/**
 * Generates an Excel formula to calculate the VAT percentage by dividing the VAT value
 * in the current column by the management fee value in the same column.
 *
 * The formula includes error handling with IFERROR to return 0 if the denominator is zero or missing.
 *
 * Example (with hasCatchUp = true):
 *   rowIndex = 10, columnIndex = 2
 *   => VAT row will be 11 (10 + 1)
 *   => mgFeeRow will be 8 (10 - 2)
 *   => columnLetter = 'C'
 *   => formula: "IFERROR(C11/C8,0)"
 *
 * @param {number} rowIndex - The base row index where the VAT row is calculated relative to.
 * @param {number} columnIndex - The column index (0-based) to convert to Excel letter.
 * @param {boolean} hasCatchUp - Flag indicating whether the sheet has a catch-up row;
 *                               changes how far up the management fee row is located.
 * @returns {string} The Excel formula as a string.
 */
export const generateFormulaForVat = (rowIndex: number, columnIndex: number, hasCatchUp: boolean): string => {
  const mgFeeRow = hasCatchUp ? rowIndex - 2 : rowIndex - 1;
  const letterNumerator = columnIndexToExcelLetter(columnIndex);
  const vatRow = rowIndex + 1;
  return `IFERROR(${letterNumerator}${vatRow}/${letterNumerator}${mgFeeRow},0)`;
};

